import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import KakaoMapViewer, { KakaoOverlayPayload } from '../components/KakaoMapViewer';

interface TerritoryMatchInfo {
  complexId: number;
  complexName: string;
  complexCode: number;
  buildingId: number;
  buildingDongNumber: number;
  matchedHouseName?: string | null;
  matchedHouseDong?: string | null;
}

interface TerritoryWithMatch {
  territoryId: string;
  territoryName: string | null;
  polygon: string | null;
  updatedAt: string | null;
  houseName: string | null;
  houseDong: string | null;
  houseDongNumber: number | null;
  complexMatches: TerritoryMatchInfo[];
  houses: Array<{
    address1: string | null;
    address2: string | null;
  }>;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface TerritoryListResponse {
  message: string;
  data: TerritoryWithMatch[];
  count: number;
  pagination?: PaginationInfo;
}

interface TerritorySingleResponse {
  message: string;
  data: TerritoryWithMatch;
}

interface TerritoryListQuery {
  search: string;
  territoryId?: string;
  onlyWithoutPolygon: boolean;
  page: number;
  pageSize: number;
}

interface TerritoryListResult {
  data: TerritoryWithMatch[];
  pagination: PaginationInfo;
}

interface PolygonSyncResult {
  territoryId: string | number;
  success: boolean;
  polygonsGenerated: number;
  matchedBuildings: number;
  warnings: string[];
  error?: string;
}

interface PolygonSyncResponse {
  message: string;
  successCount: number;
  failureCount: number;
  results: PolygonSyncResult[];
}

interface PolygonPreviewResponse {
  message: string;
  data: {
    polygon: string;
    matchedBuildings: number;
    polygonsGenerated: number;
    warnings: string[];
  };
}

const normalizeTerritory = (value: any): TerritoryWithMatch => ({
  territoryId: String(value.territoryId),
  territoryName: value.territoryName ?? null,
  polygon: value.polygon ?? null,
  updatedAt: value.updatedAt ?? null,
  houseName: value.houseName ?? null,
  houseDong: value.houseDong ?? null,
  houseDongNumber:
    typeof value.houseDongNumber === 'number' ? value.houseDongNumber : value.houseDongNumber ?? null,
  complexMatches: Array.isArray(value.complexMatches)
    ? value.complexMatches.map((match: any) => ({
        complexId: match?.complexId,
        complexName: match?.complexName,
        complexCode: match?.complexCode,
        buildingId: match?.buildingId,
        buildingDongNumber: match?.buildingDongNumber,
        matchedHouseName: match?.matchedHouseName ?? null,
        matchedHouseDong: match?.matchedHouseDong ?? null
      }))
    : [],
  houses: Array.isArray(value.houses)
    ? value.houses.map((house: any) => ({
        address1: house?.address1 ?? null,
        address2: house?.address2 ?? null
      }))
    : []
});

const formatDongLabel = (value: string | null | undefined): string => {
  if (!value) {
    return '미지정';
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return '미지정';
  }

  return trimmed.endsWith('동') ? trimmed : `${trimmed}동`;
};

const fetchTerritories = async (params: TerritoryListQuery): Promise<TerritoryListResult> => {
  const query = new URLSearchParams();
  if (params.search.trim() !== '') {
    query.append('search', params.search.trim());
  }
  if (params.onlyWithoutPolygon) {
    query.append('onlyWithoutPolygon', 'true');
  }
  if (params.territoryId && params.territoryId.trim() !== '') {
    query.append('territoryId', params.territoryId.trim());
  }
  query.append('page', String(params.page));
  query.append('pageSize', String(params.pageSize));

  const response = await fetch(`/api/map-meta/territories${query.toString() ? `?${query.toString()}` : ''}`);
  if (response.status === 503) {
    throw new Error('MAP META 데이터베이스 연결 정보가 설정되지 않았습니다.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(error.message || '외부 구역 정보를 불러오는데 실패했습니다.');
  }

  const body = (await response.json()) as TerritoryListResponse;
  const pagination: PaginationInfo = body.pagination ?? {
    page: params.page,
    pageSize: params.pageSize,
    totalCount: body.count ?? body.data.length,
    totalPages: body.count ? Math.ceil(Math.max(body.count, 0) / params.pageSize) : (body.data.length > 0 ? 1 : 0)
  };

  return {
    data: body.data.map(normalizeTerritory),
    pagination
  };
};

const fetchTerritoryDetail = async (territoryId: string): Promise<TerritoryWithMatch> => {
  const response = await fetch(`/api/map-meta/territories/${encodeURIComponent(territoryId)}`);
  if (response.status === 404) {
    throw new Error('해당 구역을 찾을 수 없습니다.');
  }
  if (response.status === 503) {
    throw new Error('MAP META 데이터베이스 연결 정보가 설정되지 않았습니다.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(error.message || '구역 정보를 불러오는데 실패했습니다.');
  }

  const body = (await response.json()) as TerritorySingleResponse;
  return normalizeTerritory(body.data);
};

const updateTerritoryPolygon = async (
  territoryId: string,
  polygon: string | null
): Promise<TerritoryWithMatch> => {
  const response = await fetch(`/api/map-meta/territories/${encodeURIComponent(territoryId)}/polygon`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ polygon })
  });

  if (response.status === 404) {
    throw new Error('구역 정보를 찾을 수 없어 저장에 실패했습니다.');
  }
  if (response.status === 503) {
    throw new Error('MAP META 데이터베이스 연결 정보가 설정되지 않았습니다.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(error.message || '구역 Polygon 정보를 저장하는 데 실패했습니다.');
  }

  const body = (await response.json()) as TerritorySingleResponse;
  return normalizeTerritory(body.data);
};

const syncTerritoryPolygonsApi = async (payload: {
  territoryIds: string[];
}): Promise<PolygonSyncResponse> => {
  const response = await fetch('/api/map-meta/territories/polygon-sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(error.message || 'Polygon 동기화에 실패했습니다.');
  }

  return response.json() as Promise<PolygonSyncResponse>;
};

const fetchTerritoryPolygonPreview = async (territoryId: string): Promise<PolygonPreviewResponse> => {
  const response = await fetch(`/api/map-meta/territories/${encodeURIComponent(territoryId)}/polygon-preview`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(error.message || 'Polygon 미리보기를 생성하는 데 실패했습니다.');
  }

  return response.json() as Promise<PolygonPreviewResponse>;
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '기록 없음';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const MapMetaPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [territoryIdInput, setTerritoryIdInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedTerritoryId, setAppliedTerritoryId] = useState<string | undefined>(undefined);
  const [onlyWithoutPolygon, setOnlyWithoutPolygon] = useState(false);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | null>(null);
  const [polygonInput, setPolygonInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedTerritories, setSelectedTerritories] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const previewCacheRef = useRef<Map<string, string>>(new Map());
  const previewWarningsRef = useRef<Map<string, string[]>>(new Map());
  const [currentPreviewWarnings, setCurrentPreviewWarnings] = useState<string[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const parsedPolygon = useMemo<{ payload: KakaoOverlayPayload | null; error: string | null }>(() => {
    if (!polygonInput || polygonInput.trim() === '') {
      return { payload: null, error: null };
    }

    try {
      const parsed = JSON.parse(polygonInput);
      if (parsed && typeof parsed === 'object') {
        return { payload: parsed as KakaoOverlayPayload, error: null };
      }
      return { payload: null, error: '유효한 지도 데이터(JSON 객체)가 아닙니다.' };
    } catch (err) {
      return {
        payload: null,
        error: err instanceof Error ? err.message : '지도 데이터(JSON) 파싱에 실패했습니다.'
      };
    }
  }, [polygonInput]);

  const polygonPayload = parsedPolygon.payload;
  const polygonParseError = parsedPolygon.error;

  const listQueryKey = useMemo(
    () => [
      'map-meta',
      'territories',
      {
        search: appliedSearch,
        ...(appliedTerritoryId ? { territoryId: appliedTerritoryId } : {}),
        onlyWithoutPolygon,
        page,
        pageSize
      }
    ],
    [appliedSearch, appliedTerritoryId, onlyWithoutPolygon, page, pageSize]
  );

  const territoriesQuery = useQuery<TerritoryListResult>({
    queryKey: listQueryKey,
    queryFn: () =>
      fetchTerritories({
        search: appliedSearch,
        ...(appliedTerritoryId ? { territoryId: appliedTerritoryId } : {}),
        onlyWithoutPolygon,
        page,
        pageSize
      }),
    placeholderData: previousData =>
      previousData ?? {
        data: [],
        pagination: {
          page,
          pageSize,
          totalCount: 0,
          totalPages: 0
        }
      },
    refetchOnWindowFocus: false
  });

  const territoriesResult =
    territoriesQuery.data ?? {
      data: [],
      pagination: {
        page,
        pageSize,
        totalCount: 0,
        totalPages: 0
      }
    };
  const territories = territoriesResult.data;
  const pagination = territoriesResult.pagination;
  const totalCount = pagination.totalCount;
  const totalPages = pagination.totalPages;
  const currentPage = pagination.page;
  const currentPageSize = pagination.pageSize;
  const selectedCount = selectedTerritories.size;
  const currentPageTerritoryIds = territories.map(item => String(item.territoryId));
  const allSelectedOnPage = currentPageTerritoryIds.length > 0 && currentPageTerritoryIds.every(id => selectedTerritories.has(id));
  const someSelectedOnPage = currentPageTerritoryIds.some(id => selectedTerritories.has(id));

  useEffect(() => {
    if (currentPage !== page) {
      setPage(currentPage);
    }
    if (currentPageSize !== pageSize) {
      setPageSize(currentPageSize);
    }
  }, [currentPage, currentPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = !allSelectedOnPage && someSelectedOnPage;
    }
  }, [allSelectedOnPage, someSelectedOnPage]);

  useEffect(() => {
    if (territories.length === 0) {
      setSelectedTerritoryId(null);
      return;
    }

    if (
      selectedTerritoryId === null ||
      !territories.some(item => item.territoryId === selectedTerritoryId)
    ) {
      const firstTerritory = territories[0];
      if (firstTerritory) {
        setSelectedTerritoryId(firstTerritory.territoryId);
      }
    }
  }, [territories, selectedTerritoryId]);

  const toggleTerritorySelection = (territoryId: string) => {
    setSelectedTerritories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(territoryId)) {
        newSet.delete(territoryId);
      } else {
        newSet.add(territoryId);
      }
      return newSet;
    });
  };

  const selectAllTerritoriesOnPage = () => {
    setSelectedTerritories(prev => {
      const newSet = new Set(prev);
      if (allSelectedOnPage) {
        currentPageTerritoryIds.forEach(id => newSet.delete(id));
      } else {
        currentPageTerritoryIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const clearSelectedTerritories = () => {
    setSelectedTerritories(new Set());
  };

  const isTerritorySelected = (territoryId: string) => selectedTerritories.has(territoryId);

  const territoryDetailQuery = useQuery<TerritoryWithMatch>({
    queryKey: ['map-meta', 'territories', 'detail', selectedTerritoryId],
    queryFn: () => fetchTerritoryDetail(selectedTerritoryId!),
    enabled: selectedTerritoryId !== null,
    initialData: () =>
      territories.find(item => item.territoryId === selectedTerritoryId) ?? undefined,
    refetchOnWindowFocus: false
  });

  const detail = territoryDetailQuery.data ?? null;
  const baselinePolygon = detail?.polygon ?? '';
  const isDirty = polygonInput !== baselinePolygon;

  const firstItemIndex = totalCount === 0 ? 0 : (currentPage - 1) * currentPageSize + 1;
  const lastItemIndex = totalCount === 0 ? 0 : Math.min(totalCount, firstItemIndex + territories.length - 1);
  const pageSizeOptions = [10, 20, 50, 100];
  const canGoPrev = currentPage > 1;
  const canGoNext = totalPages !== 0 && currentPage < totalPages;

  useEffect(() => {
    if (!detail) {
      setPolygonInput('');
      setCurrentPreviewWarnings([]);
      setIsPreviewLoading(false);
      return;
    }

    const territoryKey = String(detail.territoryId);

    if (detail.polygon && detail.polygon.trim() !== '') {
      setPolygonInput(detail.polygon);
      setCurrentPreviewWarnings([]);
      setIsPreviewLoading(false);
      previewCacheRef.current.delete(territoryKey);
      previewWarningsRef.current.delete(territoryKey);
      return;
    }

    const cachedPreview = previewCacheRef.current.get(territoryKey);
    if (cachedPreview) {
      setPolygonInput(cachedPreview);
      setCurrentPreviewWarnings(previewWarningsRef.current.get(territoryKey) ?? []);
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewLoading(true);
    setCurrentPreviewWarnings([]);
    setPolygonInput('');

    fetchTerritoryPolygonPreview(territoryKey)
      .then(response => {
        previewCacheRef.current.set(territoryKey, response.data.polygon);
        previewWarningsRef.current.set(territoryKey, response.data.warnings ?? []);
        setPolygonInput(response.data.polygon);
        setCurrentPreviewWarnings(response.data.warnings ?? []);
        if (response.data.warnings?.length) {
          toast((t) => (
            <div>
              <div className="font-semibold">Polygon 생성 경고</div>
              <ul className="mt-1 list-disc pl-5 text-xs text-gray-100">
                {response.data.warnings.slice(0, 5).map((warning, index) => (
                  <li key={`${territoryKey}-warning-${index}`}>{warning}</li>
                ))}
              </ul>
              {response.data.warnings.length > 5 && (
                <div className="mt-1 text-[10px] text-gray-200">총 {response.data.warnings.length}건의 경고가 있습니다.</div>
              )}
              <button
                type="button"
                className="mt-2 text-[11px] text-blue-200 underline"
                onClick={() => toast.dismiss(t.id)}
              >
                닫기
              </button>
            </div>
          ), { duration: 6000, style: { background: '#1f2937', color: '#fff' } });
        }
      })
      .catch(error => {
        toast.error(error instanceof Error ? error.message : 'Polygon 미리보기를 불러오는데 실패했습니다.');
      })
      .finally(() => {
        setIsPreviewLoading(false);
      });
  }, [detail?.territoryId, detail?.polygon]);

  const updatePolygonMutation = useMutation({
    mutationFn: ({ territoryId, polygon }: { territoryId: string; polygon: string | null }) =>
      updateTerritoryPolygon(territoryId, polygon),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<TerritoryListResult>(listQueryKey, oldData => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          data: oldData.data.map(item =>
            item.territoryId === variables.territoryId ? result : item
          )
        };
      });
      queryClient.setQueryData<TerritoryWithMatch>(
        ['map-meta', 'territories', 'detail', variables.territoryId],
        result
      );
      setPolygonInput(result.polygon ?? '');
      previewCacheRef.current.delete(variables.territoryId);
      previewWarningsRef.current.delete(variables.territoryId);
      setCurrentPreviewWarnings([]);
      toast.success('Polygon 정보를 저장했습니다.');
    },
    onError: error => {
      const message = error instanceof Error ? error.message : 'Polygon 정보를 저장하는 데 실패했습니다.';
      toast.error(message);
    }
  });

  const syncPolygonsMutation = useMutation({
    mutationFn: syncTerritoryPolygonsApi,
    onSuccess: (result: PolygonSyncResponse) => {
      const { successCount, failureCount, results } = result;

      if (successCount > 0) {
        toast.success(`Polygon 동기화 완료: ${successCount}건 성공${failureCount > 0 ? `, ${failureCount}건 실패` : ''}`);
      }

      if (failureCount > 0) {
        toast.error('일부 territory에 대해 Polygon 생성에 실패했습니다.');
      }

      const warningMessages = results
        .flatMap((item: PolygonSyncResult) =>
          item.warnings.map((warning: string) => `tt_id ${item.territoryId}: ${warning}`)
        );
      if (warningMessages.length > 0) {
        toast(t => (
          <div>
            <div className="font-semibold">경고</div>
            <ul className="mt-1 list-disc pl-5 text-sm">
              {warningMessages.slice(0, 5).map((warning: string, index: number) => (
                <li key={`${warning}-${index}`}>{warning}</li>
              ))}
            </ul>
            {warningMessages.length > 5 && (
              <div className="mt-1 text-xs text-gray-300">총 {warningMessages.length}건의 경고가 있습니다.</div>
            )}
            <button
              type="button"
              className="mt-2 text-xs text-blue-300 underline"
              onClick={() => toast.dismiss(t.id)}
            >
              닫기
            </button>
          </div>
        ), { duration: 7000 });
      }

      clearSelectedTerritories();
      territoriesQuery.refetch();
      if (selectedTerritoryId) {
        territoryDetailQuery.refetch();
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Polygon 동기화에 실패했습니다.';
      toast.error(message);
    }
  });

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedSearch(searchInput.trim());
    setAppliedTerritoryId(territoryIdInput.trim() === '' ? undefined : territoryIdInput.trim());
    clearSelectedTerritories();
  };

  const handleSelectTerritory = (territoryId: string) => {
    setSelectedTerritoryId(territoryId);
  };

  const handleSavePolygon = () => {
    if (!selectedTerritoryId) {
      toast.error('먼저 구역을 선택해주세요.');
      return;
    }

    const shouldClear = polygonInput.trim().length === 0;
    updatePolygonMutation.mutate({
      territoryId: selectedTerritoryId,
      polygon: shouldClear ? null : polygonInput
    });
  };

  const handleResetPolygon = () => {
    if (!detail) {
      setPolygonInput('');
      return;
    }
    setPolygonInput(detail.polygon ?? '');
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const handleSyncSelectedTerritories = () => {
    if (selectedCount === 0) {
      toast.error('동기화할 territory를 선택해주세요.');
      return;
    }

    const territoryIds = Array.from(selectedTerritories);
    syncPolygonsMutation.mutate({ territoryIds });
  };

  return (
    <div className="page-full">
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">외부 MAP META 동기화</h1>
        <p className="text-sm text-gray-600 leading-relaxed">
          외부 MariaDB의 <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">t_territory</code>와{' '}
          <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">t_house</code> 정보를 조회하여
          <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">tt_polygon</code> 값을 입력하거나 수정할 수 있습니다.
          좌측 목록에서 구역을 선택하고, 우측에서 Polygon 데이터를 편집해 저장하세요.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="xl:col-span-2 bg-white shadow-sm rounded-lg p-5">
          <div className="flex flex-col gap-4 mb-4">
            <div className="grid w-full grid-cols-1 md:grid-cols-7 gap-4 md:items-end">
              <div className="md:col-span-3">
                <label htmlFor="territory-search" className="block text-sm font-medium text-gray-700">
                  아파트 이름 / 주소 검색
                </label>
                <input
                  id="territory-search"
                  type="text"
                  value={searchInput}
                  onChange={event => setSearchInput(event.target.value)}
                  placeholder="예: 송파아파트, 잠실"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="territory-id" className="block text-sm font-medium text-gray-700">
                  tt_id (선택)
                </label>
                <input
                  id="territory-id"
                  type="text"
                  value={territoryIdInput}
                  onChange={event => setTerritoryIdInput(event.target.value)}
                  placeholder="예: 12345"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="md:col-span-2 flex items-end gap-3">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="inline-flex flex-1 justify-center items-center rounded-md border border-transparent bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  검색
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setTerritoryIdInput('');
                    setAppliedSearch('');
                    setAppliedTerritoryId(undefined);
                    setOnlyWithoutPolygon(false);
                    setPage(1);
                    clearSelectedTerritories();
                  }}
                  className="inline-flex flex-1 justify-center items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  초기화
                </button>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    checked={onlyWithoutPolygon}
                    onChange={() => setOnlyWithoutPolygon(prev => !prev)}
                  />
                  <span className="ml-2">Polygon 미입력만 보기</span>
                </label>
                <button
                  type="button"
                  onClick={() => territoriesQuery.refetch()}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  새로고침
                </button>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <div>
                  전체 {totalCount}건 중{' '}
                  {totalCount === 0
                    ? '표시할 데이터가 없습니다.'
                    : `${firstItemIndex}–${lastItemIndex}건 표시`}
                </div>
                <div>
                  조건: 검색 &quot;{appliedSearch || '전체'}&quot;
                  {appliedTerritoryId ? `, tt_id=${appliedTerritoryId}` : ''}
                  {onlyWithoutPolygon ? ', Polygon 미입력 필터 적용' : ''}
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="text-sm text-gray-600">
                선택된 territory: <span className="font-semibold text-gray-900">{selectedCount}</span>건
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSyncSelectedTerritories}
                  disabled={selectedCount === 0 || syncPolygonsMutation.isPending}
                  className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    selectedCount === 0 || syncPolygonsMutation.isPending
                      ? 'cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400'
                      : 'border border-transparent bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {syncPolygonsMutation.isPending ? '동기화 중...' : '선택한 구역 Polygon 동기화'}
                </button>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={clearSelectedTerritories}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    선택 해제
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 w-12">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelectedOnPage && currentPageTerritoryIds.length > 0}
                      onChange={event => {
                        event.stopPropagation();
                        selectAllTerritoriesOnPage();
                      }}
                    />
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">tt_id</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">아파트 / 동</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Polygon</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">수정일</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">로컬 매칭</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {territoriesQuery.isLoading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      외부 구역 정보를 불러오는 중입니다...
                    </td>
                  </tr>
                )}
                {territoriesQuery.isError && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-red-500">
                      {territoriesQuery.error instanceof Error
                        ? territoriesQuery.error.message
                        : '외부 구역 정보를 불러오는데 실패했습니다.'}
                    </td>
                  </tr>
                )}
                {!territoriesQuery.isLoading && !territoriesQuery.isError && territories.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      조건에 맞는 외부 구역이 없습니다.
                    </td>
                  </tr>
                )}
                {territories.map(item => {
                  const territoryIdKey = String(item.territoryId);
                  const isActiveRow = item.territoryId === selectedTerritoryId;
                  const isChecked = isTerritorySelected(territoryIdKey);
                  const primaryHouse = item.houses[0] ?? { address1: item.houseName, address2: item.houseDong };
                  const additionalHouseCount = Math.max(item.houses.length - 1, 0);
                  const primaryMatch = item.complexMatches[0] ?? null;
                  const additionalMatchCount = Math.max(item.complexMatches.length - 1, 0);
                  const rowClass = isActiveRow
                    ? 'bg-blue-50'
                    : isChecked
                      ? 'bg-indigo-100'
                      : 'hover:bg-gray-50';

                  return (
                    <tr
                      key={item.territoryId}
                      onClick={() => handleSelectTerritory(item.territoryId)}
                      className={`cursor-pointer ${rowClass}`}
                    >
                      <td
                        className="px-4 py-3"
                        onClick={event => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={event => {
                            event.stopPropagation();
                            toggleTerritorySelection(territoryIdKey);
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{item.territoryId}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 font-medium">{primaryHouse?.address1 ?? item.houseName ?? '미지정'}</div>
                        <div className="text-xs text-gray-500">
                          동: {formatDongLabel(primaryHouse?.address2 ?? item.houseDong)}
                          {item.territoryName ? ` · TT명: ${item.territoryName}` : ''}
                          {additionalHouseCount > 0 ? ` · 추가 ${additionalHouseCount}건` : ''}
                        </div>
                        {item.houses.length > 1 && (
                          <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                            {item.houses.slice(1).map((house, index) => (
                              <div key={`${item.territoryId}-house-${index}`}>
                                - {house.address1 ?? '미지정'} / {formatDongLabel(house.address2)}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {item.polygon ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                            입력됨
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
                            없음
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{formatDateTime(item.updatedAt)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {primaryMatch ? (
                          <div className="space-y-1">
                            <Link
                              to={`/complexes/${primaryMatch.complexId}`}
                              className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                              {primaryMatch.complexName} ({primaryMatch.complexCode})
                            </Link>
                            <div className="text-gray-500">
                              매칭 동: {primaryMatch.buildingDongNumber}
                              {additionalMatchCount > 0 ? ` · 추가 ${additionalMatchCount}건` : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">매칭 없음</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={!canGoPrev}
                className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  canGoPrev
                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                이전
              </button>
              <span className="text-sm text-gray-600">
                페이지 {totalPages === 0 ? 0 : currentPage} / {totalPages === 0 ? 0 : totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(prev => (totalPages === 0 ? prev : Math.min(totalPages, prev + 1)))}
                disabled={!canGoNext}
                className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  canGoNext
                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                다음
              </button>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>페이지 크기</span>
              <select
                value={pageSize}
                onChange={event => handlePageSizeChange(Number(event.target.value))}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {pageSizeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}개
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="xl:col-span-1 bg-white shadow-sm rounded-lg p-5">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Polygon 편집</h2>

          {!selectedTerritoryId && (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
              좌측 목록에서 편집할 구역을 선택하세요.
            </div>
          )}

          {selectedTerritoryId && territoryDetailQuery.isLoading && (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
              구역 정보를 불러오는 중입니다...
            </div>
          )}

          {selectedTerritoryId && territoryDetailQuery.isError && (
            <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-6 text-center text-red-600">
              {territoryDetailQuery.error instanceof Error
                ? territoryDetailQuery.error.message
                : '구역 정보를 불러오는데 실패했습니다.'}
            </div>
          )}

          {selectedTerritoryId && detail && (
            <div className="space-y-4">
              <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
                <div>
                  <span className="font-medium text-gray-900">tt_id:</span>{' '}
                  <span className="font-mono text-xs">{detail.territoryId}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">아파트/동 목록:</span>
                  <div className="mt-1 space-y-1">
                    {detail.houses.length === 0 && <div>미지정</div>}
                    {detail.houses.map((house, index) => (
                      <div key={`${detail.territoryId}-detail-house-${index}`}>
                        - {house.address1 ?? '미지정'} / {formatDongLabel(house.address2)}
                      </div>
                    ))}
                  </div>
                </div>
                {detail.territoryName && (
                  <div>
                    <span className="font-medium text-gray-900">TT 이름:</span> {detail.territoryName}
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-900">최근 수정:</span>{' '}
                  {formatDateTime(detail.updatedAt)}
                </div>
                <div className="border-t border-gray-200 pt-2 space-y-1">
                  <span className="font-medium text-gray-900">로컬 매칭:</span>
                  {detail.complexMatches.length === 0 && (
                    <div className="text-gray-500">매칭 없음</div>
                  )}
                  {detail.complexMatches.map(match => (
                    <div key={`${detail.territoryId}-match-${match.buildingId}`}>
                      <Link
                        to={`/complexes/${match.complexId}`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {match.complexName} ({match.complexCode}) - {match.buildingDongNumber}동
                      </Link>
                      {match.matchedHouseDong || match.matchedHouseName ? (
                        <span className="ml-2 text-xs text-gray-500">
                          (매칭 기준: {match.matchedHouseName ?? '미지정'} / {formatDongLabel(match.matchedHouseDong ?? null)})
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              {polygonParseError && (
                <div className="rounded-md bg-red-50 text-red-600 text-xs px-3 py-2">
                  지도 데이터를 파싱하는 중 오류가 발생했습니다: {polygonParseError}
                </div>
              )}

              {polygonPayload ? (
                <KakaoMapViewer data={polygonPayload} height="280px" className="mb-2" />
              ) : (
                !polygonParseError && (
                  <div className="rounded-md bg-gray-50 text-gray-500 text-xs px-3 py-2">
                    {isPreviewLoading ? '자동 Polygon 미리보기를 생성하는 중입니다...' : '미리보기할 Polygon 데이터가 없습니다.'}
                  </div>
                )
              )}

              {currentPreviewWarnings.length > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 space-y-1">
                  <div className="font-semibold">경고</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {currentPreviewWarnings.map((warning, index) => (
                      <li key={`${detail.territoryId}-preview-warning-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label htmlFor="polygon-input" className="block text-sm font-medium text-gray-700 mb-1">
                  tt_polygon 값
                </label>
                <textarea
                  id="polygon-input"
                  rows={18}
                  value={polygonInput}
                  onChange={event => setPolygonInput(event.target.value)}
                  className="block w-full rounded-md border-gray-300 font-mono text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder='예: {"type":"Polygon","coordinates":[...]}'
                />
                <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                  JSON 문자열 또는 기타 Polygon 데이터를 그대로 입력하세요. 공백 없이 비워두면 값이 삭제됩니다.
                  저장 시 외부 DB의 <code className="font-mono">tt_polygon</code> 컬럼에 반영됩니다.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleResetPolygon}
                  disabled={!isDirty}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  되돌리기
                </button>
                <button
                  type="button"
                  onClick={handleSavePolygon}
                  disabled={updatePolygonMutation.isPending || !isDirty}
                  className="inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60"
                >
                  {updatePolygonMutation.isPending ? '저장 중...' : 'Polygon 저장'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MapMetaPage;
